import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectWidgetEffectiveCols } from '../../redux/datasourceSlice.js';
import get from 'lodash/get';
import { closeDetail, saveWidgetSettings } from '../../redux/dashboardSlice.js';
import DetailSidebarSettings, { SIDEBAR_SETTING_TYPES } from '../../settings_sidebar/DetailSidebarSettings.jsx';
import { useSetArrayEntityAttribute } from '../../hooks/useSetArrayEntityAttribute.js';
import Errors from '../../../common/Errors.jsx';
import ColumnSelectSetting from '../../settings_sidebar/ag_chart/components/ColumnSelectSetting.jsx';
import TextSetting from '../../settings_sidebar/ag_chart/components/TextSetting.jsx';
import { useSetConfigError } from '../../hooks/useSetConfigError.js';
import SettingsSection from '../../settings_sidebar/common/SettingsSection.jsx';
import ToggleSetting from '../../settings_sidebar/ag_chart/components/ToggleSetting.jsx';

function ColumnSettings({ ...props }) {
  const { widgetId } = props;
  const targetEnabled = useSelector(state => state.dashboard.widgets[widgetId].settings?.targetWidget?.enabled);

  return (
    <SettingsSection>
      <TextSetting {...props} attr='headerName' label={I18n.t('header_name')} />
      <ColumnSelectSetting {...props} attr='accessor' label={I18n.t('column')} required={true} />
      <TextSetting {...props} attr='contentToReplace' label={I18n.t('content_to_replace')} disabled={!targetEnabled} />
      <ToggleSetting {...props} attr='hide' label={I18n.t('hidden')} />
    </SettingsSection>
  )
}

function ColumnOptions({ widgetId, selectedColumn }) {
  const errors = useSelector(state => state.dashboard.configErrors?.[widgetId]?.columns?.[selectedColumn?.id]) || {}
  const setAttribute = useSetArrayEntityAttribute({widgetId, entitiesSettingId: 'columns', entityId: selectedColumn.id });
  const columnDefs = useSelector(selectWidgetEffectiveCols(widgetId));

  const columnSelectOptions = useMemo(() => {
    return columnDefs.map(d => ({ label: d.name, value: d.accessor }));
  }, [columnDefs]);

  return (
    <>
      <Errors errors={errors} />
      <ColumnSettings {...{ widgetId, selectedObject: selectedColumn, columnDefs: columnSelectOptions, setAttribute }} />
    </>
  )
}

function toSnakeCase(str) {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function GridColumnSidebar({ widgetId, selectedColumn, setOpenedColumn }) {
  const dispatch = useDispatch();
  const setErrors = useSetConfigError(widgetId);
  const columns = useSelector(state => state.dashboard.widgets[widgetId].settings?.columns);

  const saveSettings = () => {
    dispatch(saveWidgetSettings());
    setOpenedColumn(null);
    dispatch(closeDetail());
  }

  
  const validate = () => {
    const columnSettings = columns.find(s => s.id === selectedColumn.id);
    const requiredKeys = ['accessor'];
    const missing = requiredKeys.filter(key => get(columnSettings, key) == null || get(columnSettings, key) === '');

    if (missing.length) {
      const missingErrors = missing.reduce((acc, key) => {
        acc[key] = [{ message: `${I18n.t('missing_required_setting')}: ${I18n.t(toSnakeCase(key))}` }];
        return acc;
      }, {});
      setErrors(`columns.${selectedColumn.id}`, missingErrors);
      return false;
    }
    setErrors(`columns.${selectedColumn.id}`, null);
    return true;
  }

  return (<>
    {selectedColumn && 
    <DetailSidebarSettings onClose={() => setOpenedColumn(null)} validate={validate} saveSettings={saveSettings} sidebarId={`${SIDEBAR_SETTING_TYPES.grid_column}-${selectedColumn.id}`}>
      <h4 className='mb-1'>{selectedColumn.name ? I18n.t('edit_column_named', {name: selectedColumn.name}) : I18n.t('edit_column_id', {id: selectedColumn.id})}</h4>
      <ColumnOptions widgetId={widgetId} selectedColumn={selectedColumn} errors={{}} />
    </DetailSidebarSettings>
    }
  </>
  )
}

export default GridColumnSidebar;